import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import {
	graphql,
	usePreloadedQuery,
	useRefetchableFragment,
	type PreloadedQuery,
} from 'react-relay';
import { ProjectDirectoryOnboardingModalComponent as ProjectDirectoryOnboardingModal } from '@jira/cross-flow-onboarding-modal/src/index.tsx';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled';
import { AnnouncerV2 } from '@atlassian/jira-accessibility/src/ui/announcer-v2/index.tsx';
import { useDirectoryRefetch } from '@atlassian/jira-directory-base-v3/src/controllers/use-directory-refetch/index.tsx';
import { useQueryFilter } from '@atlassian/jira-directory-base-v3/src/controllers/use-query-filter/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { ExperienceSuccess as PlatformExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src/DocumentTitle.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import {
	PROJECTS_DIRECTORY_EXPERIENCE,
	MANAGE_PROJECTS_DIRECTORY_EXPERIENCE,
	DEFAULT_LIMIT,
	FilterFields,
	type FilterInitialState as FilterState,
} from '@atlassian/jira-projects-directory-v3-utils/src/constants.tsx';
import { mapQueryToFilter } from '@atlassian/jira-projects-directory-v3-utils/src/utils/index.tsx';
import { pageLoad } from '@atlassian/jira-projects-directory-v3-utils/src/utils/performance-analytics.tsx';
import SubmitApdex from '@atlassian/jira-providers-spa-apdex-analytics/src/submit-apdex-mark/index.tsx';
import type { ProjectDirectoryLayoutQuery } from '@atlassian/jira-relay/src/__generated__/ProjectDirectoryLayoutQuery.graphql';
import type { ProjectDirectoryLayout$key } from '@atlassian/jira-relay/src/__generated__/ProjectDirectoryLayout.graphql';
import RefetchProjectsQuery, {
	type ProjectDirectoryRefetchQuery,
} from '@atlassian/jira-relay/src/__generated__/ProjectDirectoryRefetchQuery.graphql';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { ScrollablePage } from '@atlassian/jira-spa-apps-page-layout/src/page/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { PRODUCT_DISCOVERY_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { fetchSiteTraitsResource } from '@atlassian/jira-router-resources-recommendations/src/services/fetch-site-traits/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { RecommendedProjects } from '@atlassian/jira-projects-directory-v3-recommendations/src/ui/RecommendedProjects.tsx';
import { FilterContainer, useScopedFilter } from '../../controllers/filter-state/index.tsx';
import { AsyncEmptyView } from './empty-view/async.tsx';
import { PageErrorState } from './error-state/index.tsx';
import { Header } from './header/Header.tsx';
import messages from './messages.tsx';
import { AsyncNoPermissionViews } from './no-permission-views/async.tsx';
import { ProjectsList } from './projects-list/ProjectsList.tsx';
import { RefinementBar } from './refinement-bar/RefinementBar.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { PageErrorState } from './error-state';

type Props = {
	queryRef: PreloadedQuery<ProjectDirectoryLayoutQuery>;
	isAdminSettingsContext: boolean;
};

export const ProjectDirectoryLayout = ({
	queryRef,
	isAdminSettingsContext,
}: Props): React.JSX.Element => {
	const filterResultsAnnouncementGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.filterResultsAnnouncementGalaxia
		: messages.filterResultsAnnouncement;
	const adminDocumentTitleGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.adminDocumentTitleGalaxia
		: messages.adminDocumentTitle;
	const documentTitleGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.documentTitleGalaxia
		: messages.documentTitle;
	const experienceKey = isAdminSettingsContext
		? MANAGE_PROJECTS_DIRECTORY_EXPERIENCE
		: PROJECTS_DIRECTORY_EXPERIENCE;
	const cloudId = useCloudId();
	const syncQueryFilter = useQueryFilter();
	const onFail = useExperienceFail({
		experience: experienceKey,
	});
	const { formatMessage } = useIntl();
	const [currentProjectType] = useScopedFilter(FilterFields.ProjectType);
	const { data: siteTraits } = useResource(fetchSiteTraitsResource);
	const { siteAdminStatus } = useTenantContext();

	const shouldShowJPDCreateFirstProjectModal = () => {
		const productDiscoveryProjectFilterNotApplied =
			currentProjectType !== PRODUCT_DISCOVERY_PROJECT;
		const hasNotBeenAutoProvisionedJpd = !siteTraits?.find(
			(trait) => trait?.name === 'jpd.auto-provision.activationdate',
		);

		const hasCompletedPostExpandOnboarding = siteTraits?.find(
			(trait) =>
				trait?.name ===
				'jira_track_project_created_project_created_server_projectType_productDiscovery__first_time',
		);

		const filteredProjectsCount = dataRef?.jira?.allJiraProjects?.totalCount;

		if (!fg('jpd_ap_onboarding_kill_switcher')) {
			return false;
		}

		if (hasCompletedPostExpandOnboarding) {
			return false;
		}

		if (siteAdminStatus !== 'admin') {
			return false;
		}

		if (productDiscoveryProjectFilterNotApplied) {
			return false;
		}

		if (hasNotBeenAutoProvisionedJpd) {
			return false;
		}

		if (filteredProjectsCount !== 0) {
			return false;
		}

		return true;
	};

	/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
	const rootQueryRef = usePreloadedQuery<ProjectDirectoryLayoutQuery>(
		graphql`
			query ProjectDirectoryLayoutQuery(
				$cloudId: ID!
				$filter: JiraProjectFilterInput!
				$first: Int
				$last: Int
				$isAdminSettingsContext: Boolean!
				$isAnonymous: Boolean!
				$isRecommendationsEnabled: Boolean!
			) @preloadable {
				...Header @arguments(isAnonymous: $isAnonymous)
				...RefinementBar @arguments(isAnonymous: $isAnonymous)
				...ProjectsList130BadPattern @arguments(isAnonymous: $isAnonymous)
				...ProjectDirectoryLayout @arguments(isAdminSettingsContext: $isAdminSettingsContext)
				...EmptyView
				...NoPermissionViews
				jira {
					canBrowseProjects: permission(cloudId: $cloudId, type: BROWSE_PROJECTS) {
						hasPermission
					}
					recommendProjects(cloudId: $cloudId, activityField: UPDATED, days: 180, limit: 4)
						@include(if: $isRecommendationsEnabled)
						@optIn(to: "JiraProjectRecommendationConnection") {
						...RecommendationsList
					}
				}
			}
		`,
		queryRef,
	);
	/* eslint-enable @atlassian/relay/must-colocate-fragment-spreads */

	const [dataRef, refetch] = useRefetchableFragment<
		ProjectDirectoryRefetchQuery,
		ProjectDirectoryLayout$key
	>(
		graphql`
			fragment ProjectDirectoryLayout on Query
			@argumentDefinitions(isAdminSettingsContext: { type: "Boolean!" })
			@refetchable(queryName: "ProjectDirectoryRefetchQuery") {
				jira {
					allJiraProjects(cloudId: $cloudId, filter: $filter, first: $first, last: $last) {
						...ProjectsList @arguments(isAdminSettingsContext: $isAdminSettingsContext)
						totalCount
					}
				}
			}
		`,
		rootQueryRef,
	);

	const prevDataRef = usePrevious(dataRef);

	const [{ isFetching, errors }, { refetchData }] = useDirectoryRefetch({
		refetch,
		query: RefetchProjectsQuery,
	});

	const handleFilterUpdate = useCallback(
		(filter: FilterState) => {
			const { [FilterFields.Page]: currentPage } = filter;

			refetchData({
				variables: {
					cloudId,
					filter: mapQueryToFilter(filter, { cloudId }),
					first: DEFAULT_LIMIT * parseInt(currentPage, 10),
					last: DEFAULT_LIMIT,
					isAdminSettingsContext,
				},
				options: { fetchPolicy: 'network-only' },
			});
			syncQueryFilter(filter);
		},
		[cloudId, refetchData, syncQueryFilter, isAdminSettingsContext],
	);

	const isSiteAdmin = useIsSiteAdmin();
	const isJiraAdmin = useIsAdmin();

	const canBrowseProjects =
		isSiteAdmin || isJiraAdmin || rootQueryRef?.jira?.canBrowseProjects?.hasPermission;

	if (!canBrowseProjects) {
		return (
			<HeaderlessContainer>
				<AsyncNoPermissionViews dataRef={rootQueryRef} />
				<PlatformExperienceSuccess
					experience={experienceKey}
					attributes={{ view: 'no-permission' }}
				/>
			</HeaderlessContainer>
		);
	}

	if (errors) {
		onFail('projects-directory-v3.page', errors);
		return (
			<HeaderlessContainer>
				<PageErrorState />
			</HeaderlessContainer>
		);
	}

	return (
		<JSErrorBoundary id="page-layout" packageName="jiraProjectsDirectoryV3" onError={onFail}>
			<FilterContainer
				isGlobal
				onUpdate={handleFilterUpdate}
				isAdminSettingsContext={isAdminSettingsContext}
			>
				<Box paddingInline={isVisualRefreshEnabled() ? 'space.300' : 'space.500'}>
					<DocumentTitle
						title={
							isAdminSettingsContext
								? formatMessage(adminDocumentTitleGalaxia)
								: formatMessage(documentTitleGalaxia)
						}
					/>
					<Header dataRef={rootQueryRef} isAdminSettingsContext={isAdminSettingsContext} />
					{rootQueryRef?.jira?.recommendProjects &&
						expVal('project_directory_relevancy_sorting_recommend', 'isEnabled', false) && (
							<RecommendedProjects recommendationsRef={rootQueryRef.jira.recommendProjects} />
						)}
					<RefinementBar dataRef={rootQueryRef} />
				</Box>
				<ScrollablePage>
					<AnnouncerV2
						message={formatMessage(filterResultsAnnouncementGalaxia, {
							itemCount: dataRef?.jira?.allJiraProjects?.totalCount || 0,
						})}
						shouldAnnounce={!isFetching && prevDataRef !== dataRef}
					/>

					{dataRef?.jira?.allJiraProjects && dataRef?.jira?.allJiraProjects?.totalCount ? (
						<>
							<ProjectsList
								isLoading={isFetching}
								isAdminSettingsContext={isAdminSettingsContext}
								projectsRef={dataRef?.jira?.allJiraProjects}
								rootQueryRef={rootQueryRef}
							/>
							<PlatformExperienceSuccess
								experience={experienceKey}
								attributes={{ view: 'projects-list' }}
							/>
						</>
					) : (
						<>
							<AsyncEmptyView dataRef={rootQueryRef} />
							<PlatformExperienceSuccess
								experience={experienceKey}
								attributes={{ view: 'empty' }}
							/>
						</>
					)}
				</ScrollablePage>
				{shouldShowJPDCreateFirstProjectModal() && (
					<ProjectDirectoryOnboardingModal
						targetProduct="jira-product-discovery"
						sourceComponent="ProjectsDirectoryV3Filter"
					/>
				)}
			</FilterContainer>
			<SpaStatePageReady />
			<SubmitApdex
				appName="projects-directory-v3"
				metricKey="jira.directories.projects-v3"
				isExpectedToHaveSsr
				// @ts-expect-error - TS2322
				metric={pageLoad}
			/>
			<SubProductUpdater subProduct="platform" />
		</JSErrorBoundary>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderlessContainer = styled.div({
	'padding-top': token('space.800'),
});
